import {
  authenticatedFetch,
  extractApiErrorMessage,
  resolvePeriverseApiBaseUrl,
} from '../shared';
import {
  InstrumentTaxonomyClassDtoSchema,
  PaginatedUntrackedAssetCatalogResponseDtoSchema,
  UntrackedAssetCatalogDtoSchema,
  UntrackedAssetCatalogTaxonomyResponseDtoSchema,
  type CreateUntrackedAssetCatalogDto,
  type InstrumentTaxonomyClassDto,
  type PaginatedUntrackedAssetCatalogResponseDto,
  type UntrackedAssetCatalogDto,
  type UntrackedAssetCatalogFilterParams,
  type UntrackedAssetCatalogTaxonomyResponseDto,
  type UpdateUntrackedAssetCatalogDto,
  type UpdateUntrackedAssetCatalogTaxonomyDto,
} from '@peritasai/types';
import { z } from 'zod';

type BaseUrlResolver = () => string;

export class UntrackedAssetCatalogApiClient {
  constructor(
    private readonly resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
  ) {}

  async getUntrackedAssetCatalog(
    options: UntrackedAssetCatalogFilterParams = {}
  ): Promise<PaginatedUntrackedAssetCatalogResponseDto> {
    const queryParams = new URLSearchParams();

    if (options.search) {
      queryParams.append('search', options.search);
    }

    if (options.type) {
      queryParams.append('type', options.type);
    }

    const page = options.page !== undefined ? options.page + 1 : 1;
    const limit = options.limit ?? 10;
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    const url = `${this.resolveBaseUrl()}/untracked-asset-catalog?${queryParams.toString()}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return PaginatedUntrackedAssetCatalogResponseDtoSchema.parse(await response.json());
  }

  async createUntrackedAssetCatalog(
    dto: CreateUntrackedAssetCatalogDto
  ): Promise<UntrackedAssetCatalogDto> {
    const url = `${this.resolveBaseUrl()}/untracked-asset-catalog`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return UntrackedAssetCatalogDtoSchema.parse(await response.json());
  }

  async updateUntrackedAssetCatalog(
    id: string,
    dto: UpdateUntrackedAssetCatalogDto
  ): Promise<UntrackedAssetCatalogDto> {
    const url = `${this.resolveBaseUrl()}/untracked-asset-catalog/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return UntrackedAssetCatalogDtoSchema.parse(await response.json());
  }

  async updateTaxonomyLink(
    catalogItemId: string,
    dto: UpdateUntrackedAssetCatalogTaxonomyDto
  ): Promise<UntrackedAssetCatalogTaxonomyResponseDto> {
    const url = `${this.resolveBaseUrl()}/instrument-taxonomy/catalog-items/${catalogItemId}/taxonomy-class`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return UntrackedAssetCatalogTaxonomyResponseDtoSchema.parse(await response.json());
  }

  async getInstrumentTaxonomyClasses(): Promise<InstrumentTaxonomyClassDto[]> {
    const url = `${this.resolveBaseUrl()}/instrument-taxonomy/classes`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return z.array(InstrumentTaxonomyClassDtoSchema).parse(await response.json());
  }

  async fetchClassImage(imageId: number): Promise<Blob> {
    const url = `${this.resolveBaseUrl()}/instrument-taxonomy/class-images/${imageId}`;

    const response = await authenticatedFetch(url, { method: 'GET' });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return response.blob();
  }
}

export const createUntrackedAssetCatalogApiClient = (
  resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
): UntrackedAssetCatalogApiClient => new UntrackedAssetCatalogApiClient(resolveBaseUrl);

export const untrackedAssetCatalogApiClient = new UntrackedAssetCatalogApiClient();

export type { BaseUrlResolver };
